import { useState } from 'react'
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'

interface FAQItem {
  id: string
  question: string
  answer: string
}

const faqData: FAQItem[] = [
  {
    id: 'pricing',
    question: 'How Does Speedyssey Pricing Work For Businesses?',
    answer: 'Our pricing is tailored to your specific business needs, volume commitments, and delivery zones. We don\'t believe in one-size-fits-all pricing. After understanding your logistics requirements, we provide custom rates that scale with your business growth.'
  },
  {
    id: 'tracking',
    question: 'Can I Track Deliveries In Real Time?',
    answer: 'Yes! Every delivery comes with real-time tracking. You and your customers can monitor the delivery progress from pickup to drop-off, including live location updates, estimated arrival times, and delivery confirmations with photo proof.'
  },
  {
    id: 'minimum',
    question: 'What\'s The Minimum Number Of Items I Can Send?',
    answer: 'There\'s no strict minimum order requirement. Whether you need to send a single package or hundreds of items, Speedyssey accommodates businesses of all sizes. However, volume commitments can help you secure better pricing rates.'
  },
  {
    id: 'scheduling',
    question: 'Can I Schedule Regular Pickups From Warehouse?',
    answer: 'Absolutely! We offer flexible scheduling for regular pickups from your warehouse or fulfillment center. You can set up daily, weekly, or custom pickup schedules that align with your operational needs and peak business hours.'
  },
  {
    id: 'industries',
    question: 'What Industries Does Speedyssey Support?',
    answer: 'We serve a wide range of industries including retail & e-commerce, healthcare & medical supplies, food & beverage, fashion & apparel, electronics, and more. Our flexible platform adapts to various product types and delivery requirements.'
  },
  {
    id: 'reports',
    question: 'Can I View And Download Delivery Reports?',
    answer: 'Yes, our dashboard provides comprehensive delivery analytics and reporting. You can view delivery performance metrics, download detailed reports, track success rates, analyze delivery times, and export data for your internal systems.'
  },
  {
    id: 'delays',
    question: 'What Happens If A Delivery Is Delayed?',
    answer: 'In the rare event of a delay, you and your customer are immediately notified with updated timing. Our support team proactively manages any issues, provides alternative solutions, and ensures transparent communication throughout the resolution process.'
  }
]

function FAQ() {
  const [expanded, setExpanded] = useState<string | false>(false)

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <section id="faq" className="bg-[#191919] py-20 pb-36 px-4">
      <div className="container sm:px-10 px-3  mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Title and Description */}
          <div className="lg:col-span-1">
            <h2 
              className="text-4xl pt-6 md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Antonio, sans-serif' }}
            >
              FREQUENTLY ASKED
              QUESTIONS
            </h2>
            
            <p className="text-gray-400 text-lg leading-relaxed">
              Got questions? We've answered some of the most common ones.
            </p>
          </div>

          {/* Right Column - FAQ Accordion */}
          <div className="lg:col-span-2">
            <div className="space-y-0">
              {faqData.map((faq) => (
                <Accordion
                  key={faq.id}
                  expanded={expanded === faq.id}
                  onChange={handleChange(faq.id)}
                  sx={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    borderBottom: '1px solid #4B5563',
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: '0',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <span 
                        className="text-[#F55109] text-2xl font-bold transition-transform duration-300"
                        style={{
                          // No rotation needed, just change symbol
                        }}
                      >
                        {expanded === faq.id ? '-' : '+'}
                      </span>
                    }
                    sx={{
                      padding: '1.5rem 0',
                      minHeight: 'auto',
                      '& .MuiAccordionSummary-content': {
                        margin: '0',
                      },
                      '&.Mui-expanded': {
                        minHeight: 'auto',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      },
                    }}
                  >
                    <h3 
                      className="text-white text-lg md:text-xl font-medium pr-4"
                      style={{ fontFamily: 'Satoshi, sans-serif' }}
                    >
                      {faq.question}
                    </h3>
                  </AccordionSummary>
                  
                  <AccordionDetails
                    sx={{
                      padding: '0 0 1.5rem 0',
                    }}
                  >
                    <p className="text-gray-300 text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default FAQ